import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getOnboardingIntents } from '../api/publicRegistrationApi';
import { colorTokens } from '../app/designTokens';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { TableToolbar } from '../components/TableToolbar';
import { IconButton } from '../components/ui/IconButton';
import { TextInput } from '../components/ui/TextInput';
import { formatBillingInterval, formatModuleCode } from '../utils/systemLabels';

const { semantic } = colorTokens;

const STATUS_OPTIONS = ['previewed', 'submitted', 'used', 'expired', 'canceled'];
const BILLING_OPTIONS = ['monthly', 'yearly', 'trial'];
const MODULE_OPTIONS = ['quality', 'priority', 'schooling', 'company-portal'];

function intentStatusTone(status) {
  if (status === 'used') return 'Success';
  if (status === 'canceled' || status === 'expired') return 'Danger';
  if (status === 'submitted') return 'Info';
  if (status === 'previewed') return 'Neutral';
  return 'Neutral';
}

function ModulesCell({ codes, emptyValue }) {
  const list = Array.isArray(codes) ? codes.filter(Boolean) : [];
  if (list.length === 0) {
    return emptyValue;
  }

  const fullLabel = list.join(', ');

  return (
    <Stack
      direction="row"
      spacing={0.5}
      useFlexGap
      flexWrap="wrap"
      title={fullLabel}
      sx={{
        maxWidth: 260,
        alignItems: 'center',
      }}
    >
      {list.map((code) => (
        <StatusBadge
          key={code}
          status={code}
          category="generic"
          apiLabel={formatModuleCode(code) || code}
          size="Sm"
          tone="Neutral"
        />
      ))}
    </Stack>
  );
}

export function OnboardingIntentsPage() {
  const { t } = useTranslation(['onboarding', 'common', 'statuses']);
  const navigate = useNavigate();
  const [intents, setIntents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [billing, setBilling] = useState('');
  const [module, setModule] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');

    const params = {};
    if (search.trim()) params.search = search.trim();
    if (status) params.status = status;
    if (billing) params.billing = billing;
    if (module) params.module = module;

    getOnboardingIntents(params)
      .then((response) => setIntents(response.data || []))
      .catch(() => setError(t('onboarding:intents.load_failed')))
      .finally(() => setLoading(false));
  }, [search, status, billing, module, t]);

  const emptyValue = t('common:empty_value');
  const recordCount = intents.length;

  const columns = useMemo(
    () => [
      {
        id: 'public_id',
        label: t('onboarding:intents.columns.public_id'),
        render: (intent) => (
          <Typography
            component="span"
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              color: semantic.text.secondary,
              whiteSpace: 'nowrap',
            }}
          >
            {intent.public_id || emptyValue}
          </Typography>
        ),
      },
      {
        id: 'status',
        label: t('onboarding:intents.columns.status'),
        render: (intent) => (
          intent.status
            ? (
              <StatusBadge
                status={intent.status}
                category="onboarding_intent"
                size="Sm"
                tone={intentStatusTone(intent.status)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'modules',
        label: t('onboarding:intents.columns.modules'),
        render: (intent) => (
          <ModulesCell codes={intent.selected_module_codes} emptyValue={emptyValue} />
        ),
      },
      {
        id: 'billing',
        label: t('onboarding:intents.columns.billing'),
        render: (intent) => {
          const value = intent.selected_billing_interval;
          if (!value) return emptyValue;
          return (
            <StatusBadge
              status={value}
              category="generic"
              apiLabel={formatBillingInterval(value) || value}
              size="Sm"
              tone="Neutral"
            />
          );
        },
      },
      {
        id: 'company',
        label: t('onboarding:intents.columns.company'),
        render: (intent) => intent.company_name || emptyValue,
      },
      {
        id: 'contact',
        label: t('onboarding:intents.columns.contact'),
        render: (intent) => intent.contact_email || emptyValue,
      },
      {
        id: 'tenant_slug',
        label: t('onboarding:intents.columns.tenant_slug'),
        render: (intent) => intent.tenant_slug || emptyValue,
      },
      {
        id: 'plan',
        label: t('onboarding:intents.columns.plan'),
        render: (intent) => intent.selected_plan_code || emptyValue,
      },
      {
        id: 'used_at',
        label: t('onboarding:intents.columns.used_at'),
        render: (intent) => intent.used_at || emptyValue,
      },
      {
        id: 'created',
        label: t('onboarding:intents.columns.created'),
        render: (intent) => intent.created_at || emptyValue,
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (intent) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('onboarding:intents.detail')}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/onboarding-intents/${intent.id}`);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, navigate, t],
  );

  return (
    <Box>
      <PageHeader
        type="List"
        title={t('onboarding:intents.title')}
        subtitle={t('onboarding:intents.subtitle')}
      />

      <FilterBar type="Simple">
        <TextInput
          density="Compact"
          hideLabel
          label={t('common:search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {STATUS_OPTIONS.map((value) => (
            <FilterSelectOption key={value} value={value}>
              {t(`statuses:onboarding_intent.${value}`, value)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('onboarding:intents.billing')}
          hideLabel
          value={billing}
          onChange={(event) => setBilling(event.target.value)}
          minWidth={140}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {BILLING_OPTIONS.map((value) => (
            <FilterSelectOption key={value} value={value}>
              {formatBillingInterval(value) || value}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('onboarding:intents.module')}
          hideLabel
          value={module}
          onChange={(event) => setModule(event.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {MODULE_OPTIONS.map((value) => (
            <FilterSelectOption key={value} value={value}>
              {formatModuleCode(value) || value}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding>
        <TableToolbar
          mode="Default"
          title={t('onboarding:intents.title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('onboarding:intents.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={intents}
            emptyMessage={t('onboarding:intents.no_intents')}
            onRowClick={(intent) => navigate(`/onboarding-intents/${intent.id}`)}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                minWidth: 1180,
              },
            }}
          />
        )}
      </SectionCard>
    </Box>
  );
}
